import React, { useContext, useState } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../providers/GlobalProvider';
import { useLogAppAction } from 'hooks/useLogAppAction';
import { ExportHubAPI, ExportProduct } from 'api/ExportHubAPI';

interface Props {
  open: boolean;
  databaseId: string;
  databaseName: string;
  onClose: () => void;
}

type SectionKey = 'products' | 'licenses' | 'screenings' | 'hierarchy';
type Format = 'csv' | 'xlsx';

const SECTIONS: Array<{ key: SectionKey; label: string; sub: string }> = [
  {
    key: 'products',
    label: 'Products',
    sub: 'Every product with its identity, classification, and compliance fields.',
  },
  {
    key: 'licenses',
    label: 'Licenses + usage',
    sub: 'Each license with authorized value, consumption, remaining, and linked products.',
  },
  {
    key: 'screenings',
    label: 'Screening results',
    sub: 'Saved screening results per product, newest first.',
  },
  {
    key: 'hierarchy',
    label: 'Hierarchy (BOM)',
    sub: 'Parent → child links with quantities.',
  },
];

// The products list endpoint clamps a page at 100 rows, so a report pages
// through rather than asking for everything at once.
const PRODUCT_PAGE_SIZE = 100;
const MAX_PRODUCT_PAGES = 200;

type Row = Record<string, string | number>;

// Excel/Sheets execute a cell whose text begins with a formula character, so a
// product name or description like `=HYPERLINK(...)` becomes live code when the
// report is opened. Prefix a single quote to force it to text (OWASP CSV
// injection guidance). Leading whitespace is part of the test because the
// spreadsheet trims before deciding.
const FORMULA_START = /^[\t\r ]*[=+\-@]/;

const csvCell = (v: string | number): string => {
  const raw = `${v ?? ''}`;
  const s = typeof v === 'string' && FORMULA_START.test(raw) ? `'${raw}` : raw;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const toCsv = (rows: Row[]): string => {
  if (rows.length === 0) return '';
  const cols = Object.keys(rows[0]);
  const lines = [cols.map(csvCell).join(',')];
  rows.forEach((r) => lines.push(cols.map((c) => csvCell(r[c])).join(',')));
  return lines.join('\r\n');
};

const downloadBlob = (filename: string, blob: Blob): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Filesystem-safe stem: "Aerospace Components" → "aerospace-components".
const slug = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'database';

// A missing authorization flag is unknown, not a negative — exporting "No" for
// it would misreport the product's compliance posture.
const yesNo = (v: boolean | null | undefined): string =>
  v === null || v === undefined ? '' : v ? 'Yes' : 'No';
const list = (v: string[] | null | undefined): string =>
  Array.isArray(v) ? v.join('; ') : '';
const date = (v: string | null | undefined): string =>
  v ? moment(v).format('MM/DD/YYYY') : '';

/**
 * Builds a point-in-time export of a database from the data already in the Hub.
 * Nothing is stored server-side — each run re-reads the current records and
 * hands the file straight to the browser.
 */
const GenerateReportModal: React.FC<Props> = ({
  open,
  databaseId,
  databaseName,
  onClose,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id;
  const logAppAction = useLogAppAction();

  const [selected, setSelected] = useState<Set<SectionKey>>(
    new Set<SectionKey>(['products', 'licenses']),
  );
  const [format, setFormat] = useState<Format>('csv');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');

  const toggle = (key: SectionKey): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleClose = (): void => {
    if (busy) return;
    setProgress('');
    onClose();
  };

  /** Page through every product in the database. */
  const fetchAllProducts = async (): Promise<ExportProduct[]> => {
    if (!groupId) return [];
    const out: ExportProduct[] = [];
    let page = 1;
    let totalPages = 1;
    do {
      setProgress(`Fetching products… page ${page} of ${totalPages}`);
      // eslint-disable-next-line no-await-in-loop
      const res = await ExportHubAPI.getProducts(databaseId, groupId, {
        page: page,
        limit: PRODUCT_PAGE_SIZE,
      });
      out.push(...res.products);
      totalPages = res.pagination.totalPages || 1;
      // A report that silently stops at the cap looks like a complete export of
      // a smaller database. Fail instead, so the user sees the error rather
      // than filing partial data.
      if (totalPages > MAX_PRODUCT_PAGES) {
        throw new Error(
          `This database has ${totalPages} pages of products, over the ` +
            `${MAX_PRODUCT_PAGES}-page export limit. Narrow the database or ` +
            `export in parts.`,
        );
      }
      page += 1;
    } while (page <= totalPages);
    return out;
  };

  const buildSheets = async (): Promise<Array<{ name: string; rows: Row[] }>> => {
    if (!groupId) return [];
    const sheets: Array<{ name: string; rows: Row[] }> = [];
    const needsProducts =
      selected.has('products') || selected.has('hierarchy');
    const products = needsProducts ? await fetchAllProducts() : [];

    if (selected.has('products')) {
      sheets.push({
        name: 'Products',
        rows: products.map((p) => ({
          name: p.name,
          internal_sku: p.internal_sku || '',
          description: p.description || '',
          manufacturer: p.manufacturer || '',
          country_of_origin: p.country_of_origin || '',
          materials: p.materials || '',
          intended_end_use: p.intended_end_use || '',
          hardware_authorized: yesNo(p.hardware_authorized),
          tech_data_authorized: yesNo(p.tech_data_authorized),
          quantity: p.quantity ?? '',
          unit_of_measure: p.unit_of_measure || '',
          hts_code: p.hts_code || '',
          eccn: p.eccn || '',
          intended_destination_countries: list(
            p.intended_destination_countries,
          ),
          tier: p.tier,
          jurisdiction: p.jurisdiction,
          compliance_status: p.compliance_status,
          created_at: date(p.created_at),
          last_updated: date(p.last_updated),
        })),
      });
    }

    if (selected.has('licenses')) {
      setProgress('Fetching licenses…');
      const licenses = await ExportHubAPI.getLicenses(databaseId, groupId);
      sheets.push({
        name: 'Licenses',
        rows: licenses.map((l) => {
          const authorized = l.value;
          const used = l.value_used || 0;
          // Not clamped at zero: an overdrawn license must read as overdrawn.
          // Flooring it to 0 renders "fully consumed" and "exceeded by $40k"
          // identically, which is the one thing a compliance report can't do.
          const remaining = authorized === null ? '' : authorized - used;
          return {
            license_number: l.license_number || '',
            license_type: l.license_type,
            agency: l.agency,
            status: l.status,
            business_unit: l.business_unit || '',
            eccns: list(l.eccns),
            authorized_value: authorized ?? '',
            value_used: used,
            value_remaining: remaining,
            pct_used:
              authorized && authorized > 0
                ? `${Math.round((used / authorized) * 100)}%`
                : '',
            linked_products: l.linked_product_count,
            effective_date: date(l.effective_date),
            expiry_date: date(l.expiry_date),
          };
        }),
      });
    }

    if (selected.has('screenings')) {
      setProgress('Fetching screening results…');
      const results = await ExportHubAPI.getDatabaseScreeningResults(
        databaseId,
        groupId,
      );
      sheets.push({
        name: 'Screenings',
        rows: results.map((r) => ({
          product_name: r.product_name,
          internal_sku: r.internal_sku || '',
          check_type: r.check_type,
          status: r.status,
          risk: r.risk || '',
          summary: r.summary || '',
          screened_at: date(r.created_at),
          screened_by: r.created_by_name || '',
        })),
      });
    }

    if (selected.has('hierarchy')) {
      setProgress('Fetching hierarchy…');
      const edges = await ExportHubAPI.getBomEdges(databaseId, groupId);
      const byId = new Map(products.map((p) => [p.id, p]));
      sheets.push({
        name: 'Hierarchy',
        rows: edges.map((e) => {
          const parent = byId.get(e.parent_product_id);
          const child = byId.get(e.child_product_id);
          return {
            parent_name: parent?.name || '',
            parent_sku: parent?.internal_sku || '',
            parent_tier: parent?.tier || '',
            child_name: child?.name || '',
            child_sku: child?.internal_sku || '',
            child_tier: child?.tier || '',
            bom_quantity: e.quantity ?? '',
          };
        }),
      });
    }

    return sheets;
  };

  const generate = async (): Promise<void> => {
    if (!groupId) {
      enqueueSnackbar('No active team selected.', { variant: 'error' });
      return;
    }
    if (selected.size === 0) {
      enqueueSnackbar('Pick at least one section to include.', {
        variant: 'warning',
      });
      return;
    }
    logAppAction('export_hub:generate_report');
    setBusy(true);
    try {
      const sheets = await buildSheets();
      const stem = `${slug(databaseName)}-${moment().format('YYYY-MM-DD')}`;
      setProgress('Building the file…');

      if (format === 'xlsx') {
        const wb = XLSX.utils.book_new();
        sheets.forEach((sh) => {
          // json_to_sheet needs at least one row to infer headers; an empty
          // section still gets a sheet so the reader sees it was included.
          const ws = XLSX.utils.json_to_sheet(
            sh.rows.length ? sh.rows : [{ '(no rows)': '' }],
          );
          XLSX.utils.book_append_sheet(wb, ws, sh.name);
        });
        XLSX.writeFile(wb, `${stem}.xlsx`);
      } else {
        // One CSV per section — a single CSV can't hold four different shapes.
        sheets.forEach((sh) => {
          downloadBlob(
            `${stem}-${sh.name.toLowerCase()}.csv`,
            new Blob([toCsv(sh.rows)], {
              type: 'text/csv;charset=utf-8;',
            }),
          );
        });
      }

      const rowTotal = sheets.reduce((n, sh) => n + sh.rows.length, 0);
      enqueueSnackbar(
        `Report generated — ${rowTotal} row${
          rowTotal === 1 ? '' : 's'
        } across ${sheets.length} section${sheets.length === 1 ? '' : 's'}.`,
        { variant: 'success' },
      );
      onClose();
    } catch (err) {
      console.error('Failed to generate report:', err);
      // The page-limit error is thrown deliberately and names the remedy
      // ("narrow the database or …"). Replacing it with "please try again"
      // sends the user to retry something that cannot succeed, so surface the
      // actionable message and keep the generic one for everything else.
      const message =
        err instanceof Error &&
        err.message.startsWith('This database has ') &&
        err.message.includes('export limit')
          ? err.message
          : 'Failed to generate the report. Please try again.';
      enqueueSnackbar(message, {
        variant: 'error',
      });
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  return (
    <Modal open={open} onClose={handleClose} className='eh-modal-root'>
      <div className='eh-modal' role='dialog' aria-label='Generate Report'>
        <div className='eh-modal-head'>
          <h2 className='eh-modal-title'>Generate Report</h2>
          <button
            className='eh-modal-close'
            onClick={handleClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-modal-body'>
          <p className='eh-modal-subtitle'>
            A point-in-time export of <strong>{databaseName}</strong> built from
            its current records.
          </p>

          <div className='eh-field'>
            <span className='eh-field-label'>Include</span>
            <div className='eh-report-sections'>
              {SECTIONS.map((sec) => (
                <label className='eh-report-section' key={sec.key}>
                  <input
                    type='checkbox'
                    checked={selected.has(sec.key)}
                    onChange={() => toggle(sec.key)}
                    disabled={busy}
                  />
                  <span className='eh-report-section-text'>
                    <span className='eh-report-section-label'>{sec.label}</span>
                    <span className='eh-report-section-sub'>{sec.sub}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className='eh-field'>
            <span className='eh-field-label'>Format</span>
            <div className='eh-seg-row'>
              <button
                type='button'
                className={`eh-seg-btn ${format === 'csv' ? 'active' : ''}`}
                onClick={() => setFormat('csv')}
                disabled={busy}
              >
                CSV
              </button>
              <button
                type='button'
                className={`eh-seg-btn ${format === 'xlsx' ? 'active' : ''}`}
                onClick={() => setFormat('xlsx')}
                disabled={busy}
              >
                XLSX
              </button>
            </div>
            <p className='eh-field-hint'>
              {format === 'xlsx'
                ? 'One workbook with a sheet per section.'
                : selected.size > 1
                ? `One CSV per section — ${selected.size} files. Your browser may ask to allow multiple downloads.`
                : 'A single CSV file.'}
            </p>
          </div>
        </div>

        <div className='eh-modal-foot'>
          <button
            className='btn-outline'
            type='button'
            onClick={handleClose}
            disabled={busy}
          >
            Cancel
          </button>
          {!!progress && <span className='eh-wizard-stepcount'>{progress}</span>}
          <button
            className='btn-primary'
            type='button'
            onClick={generate}
            disabled={busy || selected.size === 0}
            style={{ opacity: busy || selected.size === 0 ? 0.6 : 1 }}
          >
            {busy ? 'Generating…' : 'Download'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default GenerateReportModal;
